import { createDefaultWidgetPage, DEFAULT_WIDGET_PAGE_ID, getDB } from './core';
import type { DataItem, HistoryItem, FolderItem, FolderDataLink } from '@/types';

export async function seedHoverTestData(): Promise<void> {
  const db = await getDB();
  const [widgetCount, historyCount] = await Promise.all([db.count('widgets'), db.count('history')]);

  const now = Date.now();
  const defaultPage = await db.get('widgetPages', DEFAULT_WIDGET_PAGE_ID);
  if (!defaultPage) {
    await db.put('widgetPages', createDefaultWidgetPage(now - 5000));
  }

  // Large-font HTML sample to reproduce top-clipping issues reliably
  const bigFontHtml: DataItem = {
    id: crypto.randomUUID(),
    type: 'text',
    label: 'big-font-html',
    mimeType: 'text/html',
    content:
      '<div style="font-family: ui-sans-serif, system-ui; padding: 0; margin: 0;">' +
      '<div style="font-size: 64px; line-height: 1; font-weight: 800;">ÁgjpqQW</div>' +
      '<div style="font-size: 20px; line-height: 1.2; margin-top: 8px;">TOP CLIP REPRO — 첫 줄 윗단이 잘리는지 확인</div>' +
      '<div style="font-size: 14px; line-height: 1.6; margin-top: 8px;">Second line to allow scrolling.</div>' +
      '</div>',
    tags: [],
    createdAt: now - 1000,
    updatedAt: now - 1000,
  };

  // If this isn't an empty state, only add the missing debug fixture (avoid polluting real data)
  if (widgetCount > 0 || historyCount > 0) {
    const existing = await db.getAll('data');
    const hasBigFont = existing.some((d) => d.label === 'big-font-html');
    if (hasBigFont) return;

    const widgets = await db.getAllFromIndex('widgets', 'by-order');
    const nextOrder = widgets.length;

    const history3: HistoryItem = {
      id: crypto.randomUUID(),
      dataId: bigFontHtml.id,
      createdAt: now - 50,
    };

    await Promise.all([
      db.put('data', bigFontHtml),
      db.put('widgets', {
        id: crypto.randomUUID(),
        pageId: DEFAULT_WIDGET_PAGE_ID,
        dataId: bigFontHtml.id,
        x: 0,
        y: 1,
        width: 1,
        height: 1,
        order: nextOrder,
      }),
      db.put('history', history3),
    ]);

    return;
  }

  // Seed on empty state
  const leftData: DataItem = {
    id: crypto.randomUUID(),
    type: 'text',
    label: 'left-widget',
    content: 'LEFT widget hover baseline sample text for shadow regression test.',
    tags: [],
    createdAt: now - 3000,
    updatedAt: now - 3000,
  };

  const rightData: DataItem = {
    id: crypto.randomUUID(),
    type: 'text',
    label: 'right-widget',
    content: 'RIGHT widget hover stress sample with longer text to reproduce hover boundary artifact and shadow bleed near folder edge.',
    tags: [],
    createdAt: now - 2000,
    updatedAt: now - 2000,
  };

  const history1: HistoryItem = {
    id: crypto.randomUUID(),
    dataId: leftData.id,
    createdAt: now - 500,
  };

  const history2: HistoryItem = {
    id: crypto.randomUUID(),
    dataId: rightData.id,
    createdAt: now - 100,
  };

  const history3: HistoryItem = {
    id: crypto.randomUUID(),
    dataId: bigFontHtml.id,
    createdAt: now - 50,
  };

  // Folder test data
  const folder1: FolderItem = {
    id: crypto.randomUUID(),
    name: '프로젝트 메모',
    parentId: null,
    order: 0,
    createdAt: now - 4000,
  };

  const folder2: FolderItem = {
    id: crypto.randomUUID(),
    name: '참고 자료',
    parentId: null,
    order: 1,
    createdAt: now - 3500,
  };

  const subFolder: FolderItem = {
    id: crypto.randomUUID(),
    name: 'HTML 샘플',
    parentId: folder1.id,
    order: 0,
    createdAt: now - 3200,
  };

  const folderLink1: FolderDataLink = {
    id: crypto.randomUUID(),
    folderId: folder1.id,
    dataId: leftData.id,
    order: 0,
  };

  const folderLink2: FolderDataLink = {
    id: crypto.randomUUID(),
    folderId: folder2.id,
    dataId: rightData.id,
    order: 0,
  };

  const folderLink3: FolderDataLink = {
    id: crypto.randomUUID(),
    folderId: subFolder.id,
    dataId: bigFontHtml.id,
    order: 0,
  };

  await Promise.all([
    db.put('data', leftData),
    db.put('data', rightData),
    db.put('data', bigFontHtml),
    db.put('widgets', {
      id: crypto.randomUUID(),
      pageId: DEFAULT_WIDGET_PAGE_ID,
      dataId: leftData.id,
      x: 0,
      y: 0,
      width: 1,
      height: 1,
      order: 0,
    }),
    db.put('widgets', {
      id: crypto.randomUUID(),
      pageId: DEFAULT_WIDGET_PAGE_ID,
      dataId: rightData.id,
      x: 1,
      y: 0,
      width: 1,
      height: 1,
      order: 1,
    }),
    db.put('widgets', {
      id: crypto.randomUUID(),
      pageId: DEFAULT_WIDGET_PAGE_ID,
      dataId: bigFontHtml.id,
      x: 0,
      y: 1,
      width: 1,
      height: 1,
      order: 2,
    }),
    db.put('history', history1),
    db.put('history', history2),
    db.put('history', history3),
    db.put('folders', folder1),
    db.put('folders', folder2),
    db.put('folders', subFolder),
    db.put('folderLinks', folderLink1),
    db.put('folderLinks', folderLink2),
    db.put('folderLinks', folderLink3),
  ]);
}
